//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import AsyncComboBox from "eai-configurator-ui/components/base/AsyncComboBox";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  table: {
    minWidth: 500,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: "1 1 100%",
  },
  message: {
    marginBottom: "10px",
  },
  noBorder: {
    border: "none",
  },
  checkboxHeader: {
    width: 76,
    display: "inline-grid",
    textAlign: "center",
    paddingTop: 10
  },
  checkboxColumn: {
    width: 76,
    display: "inline-grid",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10
  }
}));

export default function ManageLoaderUsersTable({users, setUsers, layoutToolEnabled, automationEnabled}) {
  const classes = useStyles();

  const [forceTeamOptions, setForceTeamOptions] = React.useState([]);

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  const onOptionsInputChange = (newOptions) => {
    console.log(">> onOptionsInputChange(newOptions: " + newOptions + ")");
    const newOptionArray = [...forceTeamOptions, ...newOptions].filter(onlyUnique);
    console.log(">> onOptionsInputChange.setForceTeamOptions(" + newOptionArray + ")");
    setForceTeamOptions(newOptionArray);
  }

  const setTeams = (user, teams) => {
    const userClone = {...user};
    userClone.teams = teams;

    const usersClone = [...users];
    const index = users.indexOf(user);
    if (index >= 0) {
      usersClone[index] = userClone;
    }
    setUsers(usersClone);
  };

  const setUserProperty = (index, user, propertyName, propertyValue) => {
    const updatedUser = {...user};
    updatedUser[propertyName] = propertyValue
    const updatedUsers = [...users];
    updatedUsers.splice(index, 1, updatedUser);
    setUsers(updatedUsers);
  }

  const isTableEmpty = users.length === 0;
  return (
    <div className={classes.root}>
      <Typography className={classes.message} variant="body1">
        Please select the users who can use this application and their privileges. Hover over the column headers for more information.
      </Typography>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-labelledby="Users">
            {!isTableEmpty && (
              <TableHead>
                <TableRow>
                  <Tooltip title="The full user name" arrow>
                    <TableCell>User Name</TableCell>
                  </Tooltip>
                  <Tooltip title="The user login" arrow>
                    <TableCell align="center">Login</TableCell>
                  </Tooltip>
                  <Tooltip title="The instance associated with this username. Each username must be configured in each instance" arrow>
                    <TableCell align="center">Instance</TableCell>
                  </Tooltip>
                  <Tooltip title="Authorized users can access the P6-Loader" arrow>
                    <TableCell align="center" class={classes.checkboxHeader}>
                      Authorized
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="User can view all tasks from all users" arrow>
                    <TableCell align="center" class={classes.checkboxHeader}>
                      All Tasks
                    </TableCell>
                  </Tooltip>
                  {automationEnabled && (
                    <Tooltip title="User has access to the automation module" arrow>
                      <TableCell align="center" class={classes.checkboxHeader}>
                        Automation
                      </TableCell>
                    </Tooltip>)}
                  {layoutToolEnabled && (
                    <Tooltip title="User has access to the layout module" arrow>
                      <TableCell align="center" class={classes.checkboxHeader}>
                        Layouts
                      </TableCell>
                    </Tooltip>)}
                  <Tooltip title="User is a superuser" arrow>
                    <TableCell align="center" class={classes.checkboxHeader}>
                      Superuser
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Control the teams associated to each user" arrow>
                    <TableCell align="left">Teams</TableCell>
                  </Tooltip>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {users.map((user, index) => (
                <TableRow hover role="task" tabIndex={-1} key={index}>
                  <TableCell align="left" component="td" padding="default">
                    {user.longName}
                  </TableCell>
                  <TableCell align="left" component="td" padding="default">
                    {user.userName}
                  </TableCell>
                  <TableCell align="left" component="td" padding="default">
                    {user.instanceName}
                  </TableCell>

                  <TableCell align="center" component="td" class={classes.checkboxColumn}>
                    <Checkbox
                      color="primary"
                      checked={user.licensed}
                      onChange={(event) => {
                        setUserProperty(index, user, "licensed", event.target.checked)
                      }}/>
                  </TableCell>
                  <TableCell align="center" component="td" class={classes.checkboxColumn}>
                    <Checkbox
                      color="primary"
                      checked={user.viewAllTasksPrivilege}
                      onChange={(event) => {
                        setUserProperty(index, user, "viewAllTasksPrivilege", event.target.checked)
                      }}/>
                  </TableCell>
                  {automationEnabled && (<TableCell align="center" component="td" class={classes.checkboxColumn}>
                    <Checkbox
                      color="primary"
                      checked={user.manageAutomationPrivilege}
                      onChange={(event) => {
                        setUserProperty(index, user, "manageAutomationPrivilege", event.target.checked)
                      }}/>
                  </TableCell>)}
                  {layoutToolEnabled && (<TableCell align="center" component="td" class={classes.checkboxColumn}>
                    <Checkbox
                      color="primary"
                      checked={user.manageLayoutsPrivilege}
                      onChange={(event) => {
                        setUserProperty(index, user, "manageLayoutsPrivilege", event.target.checked)
                      }}/>
                  </TableCell>)}
                  <TableCell align="center" component="td" class={classes.checkboxColumn}>
                    <Checkbox
                      color="primary"
                      checked={user.superuserPrivilege}
                      disabled={user.preconfiguredSuperuserPrivilege}
                      onChange={(event) => {
                        setUserProperty(index, user, "superuserPrivilege", event.target.checked)
                      }}/>
                  </TableCell>

                  <TableCell align="right" component="td" padding="default">
                    <AsyncComboBox
                      url={"cloudapi/configuration/teams"}
                      textFieldProps={{
                        label: "",
                        fullWidth: true,
                        size: "small",
                        style: {
                          minWidth: "200px",
                          marginTop: 0,
                          marginBottom: 0,
                          borderColor: "red",
                        },
                      }}
                      value={user.teams || []}
                      onChange={(event, value) => setTeams(user, value)}
                      forceOptions={forceTeamOptions}
                      onOptionsInputChange={onOptionsInputChange}
                      freeSolo
                      multiple
                      autoSelect={false}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {isTableEmpty && (
                <TableRow style={{height: 53}}>
                  <TableCell>
                    <Typography align="center" color="textSecondary">
                      No users
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
